export type CaseStatus =
  | "open"
  | "interview_submitted"
  | "voice_over_submitted"
  | "theme_selection_submitted"
  | "summary_submitted"
  | "closed";
export type ArchiveReason = "closed" | "expired" | "cleaned";

// A case is locked once the summary has been submitted: the submission is
// either still in flight ("summary_submitted") or finished ("closed"). Edits
// made after that point never reach CompetenSYS, so every mutating control
// must be hidden or disabled for both statuses.
export const isCaseLocked = (status?: CaseStatus): boolean =>
  status === "summary_submitted" || status === "closed";

export interface CaseListItem {
  id: string;
  status?: CaseStatus;
  archive_reason?: ArchiveReason | null;
  can_inspect?: boolean;
  created_at?: string;
  submitted_at?: string | null;
  name?: string;
}

export interface Case extends CaseListItem {
  updated_at?: string;
  user_id?: string;
  project_questionnaire_id?: number;
  candidate_id?: number;
  voice_over?: Recording;
  interview?: Recording;
}

export interface Recording {
  recording_type: string;
  generation: number;
  is_active: boolean;
  has_final_chunk: boolean;
  text: string | null;
  redacted_transcript: string | null;
  executive_summary: string | null;
  has_raw_audio: boolean;
  can_play_audio: boolean;
  can_retranscribe: boolean;
  has_completed_all_chunks: boolean;
  task_ids: string[];
}

export interface TaskResponse {
  task_id: string;
}

export interface Entity {
  start: number;
  end: number;
  text: string;
  label: string;
  score: number;
}

export interface AnonymizationResult {
  anonymized_text: string;
  normalized_text: string;
  entities: Entity[];
}

export interface TranscriptResponse {
  text: string;
  executive_summary: string | null;
  language: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskStatus extends Partial<TranscriptResponse> {
  // Local UI/polling state fields
  id?: string;
  audio_chunks?: Array<{
    id: string;
    status: string;
  }>;
  all_workflows_completed?: boolean;
  has_final_chunk?: boolean;
  recording_type?: string;
  anonymized_text?: string;
  normalized_text?: string;
  anonymization_result?: AnonymizationResult;
}

// Add this to your types/index.ts file or create a new constants file
export interface CaseStatusConfig {
  color: string;
  displayName: string;
  uri?: string;
}

export const caseItemStatus: Record<string, CaseStatusConfig> = {
  // Badge colours come from the Gesprekskompas design; the per-status labels
  // are main's, which distinguish the stages the design pass predates.
  open: {
    color: "bg-brand-red/90 text-brand-beige",
    displayName: "Open",
    uri: "/{id}/record",
  },
  interview_submitted: {
    color: "bg-[#F09888] text-brand-beige",
    displayName: "Persoonlijke opmerkingen",
    uri: "/case/{id}",
  },
  voice_over_submitted: {
    color: "bg-[#F09888] text-brand-beige",
    displayName: "Thema selectie",
    uri: "/case/{id}/results",
  },
  theme_selection_submitted: {
    color: "bg-[#F09888] text-brand-beige",
    displayName: "Samenvatting",
    uri: "/case/{id}/summary",
  },
  summary_submitted: {
    color: "bg-[#F09888] text-brand-beige",
    displayName: "Bezig met verzenden",
    uri: "/case/{id}/summary",
  },
  closed: {
    color: "bg-[#48ACA6] text-brand-beige",
    displayName: "Verzonden",
    uri: "/case/{id}/summary",
  },
};

// Helper function to get status config with URI replacement
export const getStatusConfig = (status: string, caseItem: Case): CaseStatusConfig => {
  const config = caseItemStatus[status] || {
    color: "bg-gray-100 text-gray-800",
    displayName: status,
    uri: "/case/{id}",
  };

  // Create a new object and replace placeholders in URI
  return {
    ...config,
    uri: config.uri?.replace("{id}", caseItem.id),
  };
};
